import { type KeyboardEventHandler } from "react";
import { Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * The one search field.
 *
 * Two dialects had grown apart: Browse/Videos/Study Groups used a `rounded-xl`
 * bordered field on a `stone-50` wash, while the vocabulary toolbars used a
 * borderless `rounded-full` pill on `muted`. Same control, two shapes, and the
 * clear affordance differed too (a bare glyph in one, a tinted disc in the
 * other).
 *
 * This is the canonical field: the bordered `rounded-xl` surface (it is a
 * *field*, like every other input in the form system), with the one detail
 * the pill version got right — the magnifier takes the primary colour while
 * the field has focus.
 *
 * Clearing is immediate: callers that debounce their query must still show the
 * cleared value at once, which is why `onChange("")` is called directly.
 */

export interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** Defaults to the placeholder — a field needs a name either way. */
  ariaLabel?: string;
  /** Overrides the shared "Clear search" label when a surface says it better. */
  clearLabel?: string;
  dataTour?: string;
  id?: string;
  className?: string;
  inputClassName?: string;
  /** Focus the field on mount — only for a surface whose whole job is typing. */
  autoFocus?: boolean;
  /** Dark, compact variant for a dense toolbar row. */
  size?: "md" | "sm";
  /** Enter/Escape handling stays with the caller that owns the query. */
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
}

const SearchField = ({
  value,
  onChange,
  placeholder,
  ariaLabel,
  clearLabel,
  dataTour,
  id,
  className,
  inputClassName,
  autoFocus = false,
  size = "md",
  onKeyDown,
}: SearchFieldProps) => {
  const { t } = useTranslation();

  return (
    <div className={cn("group relative", className)}>
      <Search
        className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground transition-colors group-focus-within:text-primary"
        aria-hidden="true"
      />
      <Input
        type="text"
        id={id}
        data-tour={dataTour}
        autoFocus={autoFocus}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder}
        className={cn(
          "rounded-xl border-stone-200 bg-stone-50/80 pl-10 pr-10 text-sm",
          "dark:border-stone-700 dark:bg-stone-800/30",
          "focus-visible:bg-background focus-visible:ring-2 focus-visible:ring-primary/20",
          size === "md" ? "h-10" : "h-9",
          inputClassName
        )}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label={clearLabel ?? t("common.actions.clearSearch", "Clear search")}
          className="absolute right-2.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

export { SearchField };
